import { useNotification } from 'hooks'
import s from '../../../commonStyles.modules.scss'
import { useMemo, useState } from 'react'
import { Table } from 'components/core-ui/Table'
import { drawerFilters, getColumns, toolbarActions } from './helpers'
import { makeCoreGetFileRequest, makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { formatAndDownloadResponse, openPdfInNewTab } from 'utils/helpers'
import { EditActionIcon } from '../../../../../icons'
import { EditReportForm } from './EditReportForm'
import { getReturoNoticeStatusOptions, getShopOptionsWithReturoId } from '../../api'
import PrintActionIcon from '../../../../../icons/PrintActionIcon'

const Reports = () => {
  const { showNotification } = useNotification()
  const [tableItems, setTableItems] = useState<ReturoReport[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [isExcelExportLoading, setIsExcelExportLoading] = useState<boolean>(false)

  const { data: shopOptions } = getShopOptionsWithReturoId(showNotification)

  const { data: statusOptions } = getReturoNoticeStatusOptions(showNotification)

  const [selectedRow, setSelectedRow] = useState<ReturoReport>()

  const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)

  const openNewModal = (): void => setIsNewModalOpen(true)
  const closeNewModal = (): void => setIsNewModalOpen(false)

  const openEditModal = (rowId: number | string): void => {
    selectRow(rowId)
    setIsEditModalOpen(true)
  }
  const closeEditModal = (): void => setIsEditModalOpen(false)

  const selectRow = (rowId: number | string) => {
    const row = tableItems.find(item => item.id === rowId)
    if (row) {
      setSelectedRow(row)
    }
  }

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/shop-reception/returo-notice',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `Error while fetching table data. ${formatErrorMessage(error)}`,
        type: 'error'
      })
      setIsLoading(false)
    }
  }

  const onClickPrint = async noticeId => {
    try {
      const response = await makeCoreGetFileRequest(
        `/shop-reception/returo-notice/notice-print/${noticeId}`,
        {
          responseType: 'blob'
        }
      )

      openPdfInNewTab(response)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la printare. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const exportFileExcel = async (tableState, e) => {
    setIsExcelExportLoading(true)
    const { filters, order, orderBy } = tableState
    const url = toTableGETUrl({
      url: '/shop-reception/returo-notice/export',
      tableFilters: filters,
      order,
      orderBy
    })

    try {
      const response = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })
      formatAndDownloadResponse(response)
      setIsExcelExportLoading(false)
    } catch (error) {
      setIsExcelExportLoading(false)
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const columns = useMemo(() => getColumns(), [])

  const toolbar = useMemo(
    () =>
      toolbarActions({
        totalCount,
        exportFileExcel: exportFileExcel,
        isExcelExportLoading,
        openNewModal
      }),
    [totalCount, isExcelExportLoading]
  )

  const filters = useMemo(
    () => drawerFilters(shopOptions, statusOptions),
    [shopOptions, statusOptions]
  )

  return (
    <div className={s.mainContainer}>
      <EditReportForm
        isModalOpen={isNewModalOpen}
        closeModal={closeNewModal}
        setShouldRefreshData={setShouldRefreshData}
      />
      <EditReportForm
        isModalOpen={isEditModalOpen}
        closeModal={closeEditModal}
        setShouldRefreshData={setShouldRefreshData}
        editItem={selectedRow}
        isEdit
      />
      <Table
        hideCheckbox
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        rowActions={[
          {
            label: 'Print',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#16a085',
            desktopIcon: <PrintActionIcon />,
            onClick: (e, rowId) => onClickPrint(rowId)
          },
          {
            label: 'Edit',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#16a085',
            desktopIcon: <EditActionIcon />,
            onClick: (e, rowId) => openEditModal(rowId)
          }
        ]}
        toolbarActions={toolbar}
        rowActionsWidth="130px"
        filters={filters}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default Reports
